/**
 * /reports/book-tax-difference — GAAP vs tax depreciation, per asset.
 *
 * Per-asset GAAP (posted + scheduled for the selected year) vs tax total,
 * the year's difference, and cumulative difference since acquisition. See
 * MONEY-TAX-DEPRECIATION-001-spec.md §5.
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useQuery, queryOptions } from "@tanstack/react-query";
import {
  Alert, Box, Button, CircularProgress, Paper, Stack, Table,
  TableBody, TableCell, TableContainer, TableHead, TableRow, TextField,
  Typography,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import BalanceIcon from "@mui/icons-material/Balance";
import { api } from "@/lib/api";

export const Route = createFileRoute("/_auth/reports/book-tax-difference")({
  component: BookTaxDifferenceReportPage,
});

interface BookTaxDifferenceRow {
  asset_id: string;
  asset_no: string;
  description: string;
  gaap_amount: number;
  tax_amount: number;
  difference: number;
  cumulative_gaap: number;
  cumulative_tax: number;
  cumulative_difference: number;
}

interface BookTaxDifferenceReport {
  year: number;
  rows: BookTaxDifferenceRow[];
  total_gaap: number;
  total_tax: number;
  total_difference: number;
}

const CURRENT_YEAR = new Date().getFullYear();
const MIN_YEAR = 1990;
const MAX_YEAR = 2099;

const bookTaxDifferenceReportQueryOptions = (year: number) =>
  queryOptions({
    queryKey: ["reports", "book-tax-difference", year],
    queryFn: () => api<BookTaxDifferenceReport>(`/api/reports/book-tax-difference?year=${year}`),
  });

function fmtMoney(v: string | number | null | undefined) {
  if (v === null || v === undefined) return "—";
  const n = typeof v === "string" ? Number(v) : v;
  if (!Number.isFinite(n)) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);
}

function BookTaxDifferenceReportPage() {
  const [year, setYear] = useState(CURRENT_YEAR);
  const reportQ = useQuery(bookTaxDifferenceReportQueryOptions(year));
  const rows = reportQ.data?.rows ?? [];
  const totals = {
    gaap_total: reportQ.data?.total_gaap ?? rows.reduce((s, r) => s + Number(r.gaap_amount ?? 0), 0),
    tax_total: reportQ.data?.total_tax ?? rows.reduce((s, r) => s + Number(r.tax_amount ?? 0), 0),
    difference: reportQ.data?.total_difference ?? rows.reduce((s, r) => s + Number(r.difference ?? 0), 0),
    cumulative_difference: rows.reduce((s, r) => s + Number(r.cumulative_difference ?? 0), 0),
  };

  function exportCsv() {
    const header = ["asset_no", "description", "gaap_amount", "tax_amount", "difference", "cumulative_difference"];
    const lines = [header.join(",")];
    for (const r of rows) {
      const cells = [
        r.asset_no, r.description, r.gaap_amount, r.tax_amount, r.difference, r.cumulative_difference,
      ].map((c) => {
        const s = String(c);
        return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      });
      lines.push(cells.join(","));
    }
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `book-tax-difference-${year}.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Stack direction="row" alignItems="center" spacing={1}>
          <BalanceIcon color="primary" />
          <Typography variant="h5" fontWeight={600}>Book / Tax Difference</Typography>
        </Stack>
      </Stack>

      <Stack direction="row" spacing={2} mb={2} alignItems="center">
        <TextField
          label="Tax Year" type="number" size="small" sx={{ minWidth: 140 }}
          inputProps={{ min: MIN_YEAR, max: MAX_YEAR }}
          value={year}
          onChange={(e) => {
            // Skip the transient empty string while the user is retyping —
            // only commit once it parses, so backspacing doesn't snap back.
            if (e.target.value === "") return;
            const n = Number(e.target.value);
            if (Number.isFinite(n)) setYear(n);
          }}
        />
        <Box sx={{ flex: 1 }} />
        <Button startIcon={<DownloadIcon />} onClick={exportCsv} disabled={rows.length === 0}>
          Export CSV
        </Button>
      </Stack>

      {reportQ.isLoading && <CircularProgress size={20} />}
      {reportQ.error && <Alert severity="error">Failed to load report.</Alert>}
      {!reportQ.isLoading && !reportQ.error && rows.length === 0 && (
        <Alert severity="info">No book/tax difference for {year}. Assets need a tax profile set to compare.</Alert>
      )}

      {rows.length > 0 && (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow sx={{ "& th": { fontWeight: 600, bgcolor: "action.hover", fontSize: 11, textTransform: "uppercase", letterSpacing: "0.04em" } }}>
                <TableCell>Asset #</TableCell>
                <TableCell>Description</TableCell>
                <TableCell align="right">GAAP ({year})</TableCell>
                <TableCell align="right">Tax ({year})</TableCell>
                <TableCell align="right">Difference</TableCell>
                <TableCell align="right">Cumulative Difference</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.asset_id} hover>
                  <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>{r.asset_no}</TableCell>
                  <TableCell>{r.description}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.gaap_amount)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(r.tax_amount)}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", color: r.difference < 0 ? "error.main" : "text.primary" }}>
                    {fmtMoney(r.difference)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace", fontWeight: 600 }}>
                    {fmtMoney(r.cumulative_difference)}
                  </TableCell>
                </TableRow>
              ))}
              <TableRow sx={{ "& td": { fontWeight: 700, borderTop: 2, borderColor: "divider" } }}>
                <TableCell colSpan={2}>Total</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.gaap_total)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.tax_total)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.difference)}</TableCell>
                <TableCell align="right" sx={{ fontFamily: "monospace" }}>{fmtMoney(totals.cumulative_difference)}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
